import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CustomTable from "../../Component/Table";
import {
  activateDeactivateTrendingDestination,
  updateTrendingDestination,
  createTrendingDestination,
  getTrendingDestination,
} from "../../Services/destination";
import DebouncedInput from "../../Component/DebouncedInput";
import { formatDate } from "../../utils/date";
import { Button, Flex, Image, Modal, Tag, Typography, Tooltip } from "antd";
import AddModal from "./AddModal";
import EditModal from "./EditModal";

const columns = (showEditModal, handleToggleActive, deepLinkBaseUrl) => [
  {
    title: "Destination Place",
    dataIndex: "destination_text",
    key: "destination_text",
  },
  {
    title: "Destination Location",
    dataIndex: "destination_address",
    key: "destination_address",
  },
  {
    title: "Active",
    dataIndex: "is_active",
    key: "is_active",
    render: (text, record) => (
      <>
        <Tag color={record?.is_active ? "green" : "red"}>
          {record?.is_active ? "Yes" : "No"}
        </Tag>
      </>
    ),
  },
  {
    title: "Itinerary Mapped",
    dataIndex: "itinerary_id",
    key: "itinerary_id",
    render: (text, record) => (
      <>
        <Tag color={record?.itinerary_id ? "green" : "red"}>
          {record?.itinerary_id ? "Yes" : "No"}
        </Tag>
      </>
    ),
  },
  {
    title: "Image",
    dataIndex: "full_image_url",
    key: "full_image_url",
    render: (image) => (
      <Image
        src={image}
        width={50}
        height={50}
        preview={false}
      />
    ),
  },
  {
    title: "Deep Link",
    dataIndex: "deep_link",
    key: "deep_link",
    render: (_, record) => {
      if (!record.share_code) return "-";
      const generateOneLink = (baseUrl, params = {}) => {
        const queryString = Object.entries(params)
          .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
          .join('&');
        return `${baseUrl}${queryString}`;
      };

      // var value = deepLinkBaseUrl + "&c=Share Trending Destination&deep_link_value=trending&deep_link_sub1=" + record.share_code + "&deep_link_sub2=" + record.category_slug + "&deep_link_sub3=" + record.category_name + "&deep_link_sub4=" + record.category_id;
      const value = generateOneLink(deepLinkBaseUrl, {
        c: "Share Trending Destination",
        deep_link_value: "trending",
        deep_link_sub1: record.share_code,
        deep_link_sub2: record.category_slug,
        deep_link_sub3: record.category_name,
        deep_link_sub4: record.category_id,
        deep_link_sub5: record.id,
      });

      const shortLink = value.length > 15 ? `${value.slice(0, 15)}...` : value;
      
      // record.share_code iruntha show  ilana -
      return (
        <Tooltip title={value}>
          <Typography.Text
            copyable={{ text: value }}
            style={{ cursor: "pointer" }}
          >
            {shortLink}
          </Typography.Text>
        </Tooltip>
      );
    },
  },
  {
    title: "Created At",
    dataIndex: "created_at",
    key: "created_at",
    render: (text, record) =>
      record?.created_at ? formatDate(record.created_at) : "-",
  },
  {
    title: "Updated At",
    dataIndex: "updated_at",
    key: "updated_at",
    render: (text, record) =>
      record?.updated_at ? formatDate(record.updated_at) : "-",
  },
  {
    title: "Actions",
    key: "actions",
    fixed: 'right',
    render: (_, record) => (
      <Flex gap="small">
        <Button type="link" onClick={() => showEditModal(record)}>
          Edit
        </Button>
        <Button
          variant="link"
          color={record?.is_active ? "danger" : "primary"}
          onClick={() => handleToggleActive(record)}
        >
          {" "}
          {record?.is_active ? "Deactivate" : "Activate"}
        </Button>
      </Flex>
    ),
  },
];

function TrendingDestination() {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const showAddModal = () => {
    setIsAddModalVisible(true);
  };

  const showEditModal = (record) => {
    setEditingRecord(record);
    setIsEditModalVisible(true);
  };

  
  useEffect(() => {
    setCurrentPage(1); 
  }, [searchTerm]);
  
  const skip = (currentPage - 1) * pageSize;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["trending_destination", currentPage, pageSize, searchTerm],
    queryFn: () =>
        getTrendingDestination({
        skip,
        limit: pageSize,
        name: searchTerm,
      }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const deepLinkBaseUrl = data?.deep_link_base_url || "";

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const addMutation = useMutation({
    mutationFn: createTrendingDestination,
    onSuccess: () => {
      refetch();
      setIsAddModalVisible(false);
    },
  });

  const editMutation = useMutation({
    mutationFn: updateTrendingDestination,
    onSuccess: (res) => {
      if(res){
        refetch();
        setIsEditModalVisible(false);
      }
    },
  });

  const handleAddSubmit = (values) => {
    addMutation.mutate(values);
  };

  const handleEditSubmit = (id, values) => {
    editMutation.mutate({ id: id, ...values });
  };

  const activateMutation = useMutation({
    mutationFn: (data) =>
      activateDeactivateTrendingDestination(data.id, data.active),
    onSuccess: () => {
      queryClient.invalidateQueries(["trending_destination"]); // Refetch user list after update
    },
  });

  const handleToggleActive = (data) => {
    Modal.confirm({
      title: "Confirm",
      content: `Are you sure you want to ${
        data.is_active ? "deactivate" : "activate"
      } this Trending Destination?`,
      okText: data.is_active ? "Deactivate" : "Activate",
      cancelText: "Cancel",
      onOk: () => {
        activateMutation.mutate({
          id: data.id,
          active: !data.is_active, // Toggle Active Status
        });
      },
    });
  };

  return (
    <div>
      <h3>Trending Destination List</h3>
      <Flex justify="space-between">
        <DebouncedInput
          placeholder="Search by Destination Place"
          value={searchTerm}
          onChange={setSearchTerm}
          delay={1000}
          width={300}
        />
        <Button type="primary" onClick={showAddModal}>
          Add Trending Destination
        </Button>
      </Flex>
      <br />
      <CustomTable
        columns={columns(showEditModal, handleToggleActive, deepLinkBaseUrl)}
        data={data?.data || []}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: data?.total_count || 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          position: ["bottomRight"],
        }}
        onTableChange={handleTableChange}
        maxHeight={"calc(90vh - 180px)"}
      />
      <AddModal
        isModalVisible={isAddModalVisible}
        handleOk={handleAddSubmit}
        handleCancel={() => setIsAddModalVisible(false)}
        loading={addMutation.isPending}
      />
      <EditModal
        isModalVisible={isEditModalVisible}
        handleOk={handleEditSubmit}
        handleCancel={() => setIsEditModalVisible(false)}
        loading={editMutation.isPending}
        initialValues={editingRecord}
      />
    </div>
  );
}

export default TrendingDestination;
